// Everything about the display and viewing geometry that a result depends on, captured at
// the start of each test (stored with the test, shown in the Debug tab and debug report).

import type { Calibration, DisplayProfile } from "../api";
import type { StageInfo } from "../render/glStage";
import type { Geometry } from "./units";

export interface DisplaySnapshot {
  captured_at: string;
  screen_label: string | null;
  width_px: number;
  height_px: number;
  css_width: number;
  css_height: number;
  device_pixel_ratio: number;
  canvas_width_px: number | null;
  canvas_height_px: number | null;
  registered_width_px: number | null;
  registered_height_px: number | null;
  physical_width_mm: number;
  physical_height_mm: number;
  size_source: string | null;
  diagonal_in: number;
  ppi: number;
  aspect_ratio: string;
  aspect_decimal: number;
  viewing_distance_mm: number;
  px_per_deg: number;
  arcmin_per_px: number;
  color_depth: number;
  dynamic_range_high: boolean;
  color_gamut: string;
  buffer_format: string;
  output_bits: number;
  gpu: string;
  refresh_hz: number | null;
  calibration_id: number | null;
  calibration_tier: string;
  white_cd_m2: number | null;
  os_display_mode: string | null;
  background_code: number;
  background_code10: number;
  user_agent: string;
}

const NAMED: Array<[string, number]> = [
  ["16:9", 16 / 9], ["16:10", 16 / 10], ["3:2", 3 / 2], ["4:3", 4 / 3], ["21:9", 64 / 27],
  ["32:9", 32 / 9], ["5:4", 5 / 4], ["1:1", 1],
];

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

/** "16:9 (1.778)" or, for odd panels, the reduced ratio and nearest named one. */
export function aspectRatio(w: number, h: number): { label: string; decimal: number } {
  const d = Math.round((w / h) * 1000) / 1000;
  const g = gcd(w, h);
  const exact = `${w / g}:${h / g}`;
  const near = NAMED.reduce((best, cur) => (Math.abs(cur[1] - w / h) < Math.abs(best[1] - w / h) ? cur : best));
  const isNamed = Math.abs(near[1] - w / h) < 0.005;
  return { label: isNamed ? `${near[0]} (${d})` : `${exact} (${d}, near ${near[0]})`, decimal: d };
}

export function makeSnapshot(args: {
  display: DisplayProfile;
  calibration: Calibration | null;
  geometry: Geometry;
  stage: StageInfo;
  canvas: { width: number; height: number } | null;
  outputBits: number;
  backgroundCode: number;
  refreshHz: number | null;
  screenLabel: string | null;
}): DisplaySnapshot {
  const { display, calibration, geometry: g } = args;
  const diagMm = Math.hypot(display.width_mm, display.height_mm);
  const diagIn = diagMm / 25.4;
  const ppi = Math.hypot(g.screenWidthPx, g.screenHeightPx) / diagIn;
  const ar = aspectRatio(g.screenWidthPx, g.screenHeightPx);
  const r3 = (v: number) => Math.round(v * 1000) / 1000;
  return {
    captured_at: new Date().toISOString(),
    screen_label: args.screenLabel,
    width_px: g.screenWidthPx,
    height_px: g.screenHeightPx,
    css_width: window.screen.width,
    css_height: window.screen.height,
    device_pixel_ratio: window.devicePixelRatio,
    canvas_width_px: args.canvas?.width ?? null,
    canvas_height_px: args.canvas?.height ?? null,
    registered_width_px: display.width_px,
    registered_height_px: display.height_px,
    physical_width_mm: display.width_mm,
    physical_height_mm: display.height_mm,
    size_source: display.size_source,
    diagonal_in: r3(diagIn),
    ppi: r3(ppi),
    aspect_ratio: ar.label,
    aspect_decimal: ar.decimal,
    viewing_distance_mm: g.distanceMm,
    px_per_deg: r3(g.pxPerDeg),
    arcmin_per_px: r3(60 / g.pxPerDeg),
    color_depth: window.screen.colorDepth,
    dynamic_range_high: matchMedia("(dynamic-range: high)").matches,
    color_gamut: matchMedia("(color-gamut: rec2020)").matches ? "rec2020" : matchMedia("(color-gamut: p3)").matches ? "p3" : "srgb",
    buffer_format: args.stage.drawingBufferFormat,
    output_bits: args.outputBits,
    gpu: args.stage.renderer,
    refresh_hz: args.refreshHz,
    calibration_id: calibration?.id ?? null,
    calibration_tier: calibration?.tier ?? "unverified",
    white_cd_m2: calibration?.white_cd_m2 ?? null,
    os_display_mode: calibration?.os_mode || null,
    background_code: args.backgroundCode,
    background_code10: Math.round(args.backgroundCode * 1023),
    user_agent: navigator.userAgent,
  };
}
